import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import Layout from "./Layout";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ExternalLink, Github, Menu } from "lucide-react";

export default function PortfolioDevOps() {
  const { isDark } = useTheme();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = [
    {
      title: "Pulumi AWS Infrastructure Standardization (2024)",
      description: `Developed a reusable Infrastructure-as-Code framework using Pulumi to standardize AWS resource provisioning across multiple services. Created custom component resources with built-in best practices for S3 buckets, implementing patterns for frontend hosting with primary and replica buckets, automated security configurations, and consistent tagging strategies.`,
      areasOfFocus: [
        "Infrastructure as Code with Pulumi",
        "AWS resource standardization",
        "Reusable component architecture",
        "Security best practices automation",
        "Multi-environment deployment patterns",
      ],
      techStack: ["Pulumi", "TypeScript", "AWS S3", "AWS IAM", "Node.js"],
      githubUrl: "https://github.com/Mazikiii/Pulumi_Aws_Standardizing",
      liveUrl: null,
    },
  ];

  return (
    <Layout>
      {({ toggleMobileMenu }) => (
        <div className="flex flex-col items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-content">
            {/* Mobile Menu Button */}
            <div className="mb-8 lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className={`p-2 rounded-lg ${
                  isDark
                    ? "text-gray-400 hover:text-white hover:bg-gray-700"
                    : "text-text-muted hover:text-text hover:bg-gray-100"
                }`}
                aria-label="Toggle menu"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Page Header */}
            <header className="mb-12">
              {/* Breadcrumb Navigation */}
              <nav className="mb-6">
                <ol className="flex items-center gap-2 text-sm list-none">
                  <li>
                    <Link
                      to="/portfolio"
                      className={`underline transition-colors duration-base ${
                        isDark
                          ? "text-blue-400 hover:text-blue-300"
                          : "text-blue-600 hover:text-blue-800"
                      }`}
                    >
                      Portfolio
                    </Link>
                  </li>
                  <li className={isDark ? "text-gray-600" : "text-text-light"}>
                    /
                  </li>
                  <li className={isDark ? "text-white" : "text-text"}>
                    DevOps
                  </li>
                </ol>
              </nav>

              <h1
                className={`text-4xl font-display font-semibold mb-4 tracking-tight ${
                  isDark ? "text-white" : "text-text"
                }`}
              >
                DevOps Projects
              </h1>
              <p
                style={{
                  fontSize: "1rem",
                  lineHeight: "1.75",
                  fontWeight: "400",
                  color: isDark
                    ? "oklch(55% 0.013 144.91)"
                    : "oklch(35% 0.013 144.91)",
                }}
              >
                I build systems where applications deploy smoothly,
                infrastructure scales effortlessly, and issues are caught
                early—enabling teams to ship faster with confidence.
              </p>
            </header>

            {/* Projects List */}
            <div className="space-y-12">
              {projects.map((project, index) => (
                <article
                  key={index}
                  className={`pb-12 border-b last:border-b-0 ${
                    isDark ? "border-border-dark" : "border-border"
                  }`}
                >
                  {/* Project Title */}
                  <h2
                    className={`text-2xl font-display font-semibold mb-4 tracking-tight ${
                      isDark ? "text-white" : "text-text"
                    }`}
                  >
                    {project.title}
                  </h2>

                  {/* Project Description */}
                  <p
                    className="mb-6"
                    style={{
                      fontSize: "1rem",
                      lineHeight: "1.75",
                      fontWeight: "400",
                      color: isDark
                        ? "oklch(55% 0.013 144.91)"
                        : "oklch(35% 0.013 144.91)",
                    }}
                  >
                    {project.description}
                  </p>

                  {/* Areas of Focus */}
                  <div className="mb-6">
                    <h3
                      className={`text-lg font-display font-semibold mb-3 ${
                        isDark ? "text-white" : "text-text"
                      }`}
                    >
                      Areas of focus
                    </h3>
                    <ul
                      className="list-disc pl-6 space-y-2"
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.75",
                        fontWeight: "400",
                        color: isDark
                          ? "oklch(55% 0.013 144.91)"
                          : "oklch(35% 0.013 144.91)",
                      }}
                    >
                      {project.areasOfFocus.map((area, areaIndex) => (
                        <li key={areaIndex}>{area}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Tech Stack */}
                  <div className="mb-6">
                    <h3
                      className={`text-sm font-display font-semibold mb-3 uppercase tracking-wider ${
                        isDark ? "text-white" : "text-text"
                      }`}
                    >
                      Tech Stack
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {project.techStack.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className={`px-3 py-1.5 text-xs font-medium rounded ${
                            isDark
                              ? "bg-border-dark text-gray-300"
                              : "bg-secondary/20 text-text-muted"
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Project Links */}
                  <div className="flex items-center gap-4">
                    {project.githubUrl && (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 text-sm transition-colors duration-base ${
                          isDark
                            ? "text-gray-400 hover:text-primary-dark"
                            : "text-text-muted hover:text-primary"
                        }`}
                      >
                        <Github size={16} />
                        <span>View on GitHub</span>
                      </a>
                    )}
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 text-sm transition-colors duration-base ${
                          isDark
                            ? "text-gray-400 hover:text-primary-dark"
                            : "text-text-muted hover:text-primary"
                        }`}
                      >
                        <ExternalLink size={16} />
                        <span>Live Demo</span>
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
